import { Injectable } from '@angular/core';

import { Accessory, Fai, Phone, Product, ProductFactory, PromotionsService, QuoteModel } from '@bytel/bytel-sales';
import { CheckoutCartModel, LINKED_API_PRODUCT_TYPE, ProductRepository } from '@bytel/checkout';

import { Observable, Subject, forkJoin, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { BasicObject, LinkedProduct } from '../interfaces/checkout.interface';

@Injectable({
    providedIn: 'root',
})
export class AccessoryService {
    public accessoriesRefreshObs: Observable<Accessory[]>;
    protected accessories: BasicObject = {};
    private _accessoriesRefresh: Subject<Accessory[]> = new Subject();

    public constructor(private productRepo: ProductRepository) {
        this.accessoriesRefreshObs = this._accessoriesRefresh.asObservable();
    }

    public initializeAccessories(cart: CheckoutCartModel): Observable<Accessory[]> {
        return this.getAccessories(cart).pipe(
            tap((accessories) => {
                this._accessoriesRefresh.next(accessories);
            }),
        );
    }

    public hasCrosssellInCart(cart: CheckoutCartModel, quoteIndex?: number): boolean {
        const quote: QuoteModel = cart.getQuote(quoteIndex);
        const products = quoteIndex ? quote.products : cart.getAllProducts();
        const productWithAccessories = products.filter(
            (cartProduct) => ProductFactory.Is(cartProduct, Fai) || ProductFactory.Is(cartProduct, Phone),
        );

        return productWithAccessories.some((product) => product.crossSells.length > 0);
    }

    public getAccessories(cart: CheckoutCartModel, quoteIndex?: number): Observable<Accessory[]> {
        const quote: QuoteModel = cart.getQuote(quoteIndex);
        const products = quoteIndex ? quote.products : cart.getAllProducts();
        const productsWithAccessories: Product[] = this._getProducts(products);

        const accessoriesObs: Observable<Accessory[]>[] = [];
        productsWithAccessories.forEach((productWithAccessories) =>
            accessoriesObs.push(this._getAccessories(productWithAccessories, cart)),
        );

        return forkJoin(accessoriesObs).pipe(map((result) => result[0]));
    }

    public getSortedAccessories(cart: CheckoutCartModel, quoteIndex: number): Observable<Accessory[]> {
        const quote: QuoteModel = cart.getQuote(quoteIndex);
        const product: Product = quote.products.find((cartProduct) => this._canProductHaveCrosssell(cartProduct));

        if (!product || product.crossSells.length === 0) {
            return of([]);
        }

        return forkJoin([
            this.getAccessories(cart, quoteIndex),
            this.productRepo.getLinkedProductAttributes(product.gencode, LINKED_API_PRODUCT_TYPE.CROSSSELL),
        ]).pipe(
            map((result) => {
                const accessories: Accessory[] = result[0].map((accessory) => {
                    const gencode = accessory.gencode;
                    const accessoryExtraInfo = result[1].find((accessoryInfo) => accessoryInfo.gencode === gencode);
                    accessory.essential = accessoryExtraInfo.essential;
                    accessory.position = accessoryExtraInfo.position;
                    return accessory;
                });
                this._sortProductsByList(accessories, result[1]);

                return accessories;
            }),
        );
    }

    public _getProducts(products: Product[]): Product[] {
        return products.filter((cartProduct) => this._canProductHaveCrosssell(cartProduct));
    }

    private _getAccessories(equipment: Product, cartModel: CheckoutCartModel): Observable<Accessory[]> {
        if (!equipment) {
            return of([]);
        }
        const gencode: string = equipment.gencode;
        if (this.accessories[gencode]) {
            this._applyAccessoriesPromotions(cartModel, this.accessories[gencode]);
            return of(this.accessories[gencode]);
        }
        const crossSells: string[] = equipment.crossSells;
        if (crossSells.length === 0) {
            return of([]);
        }
        return this.productRepo.getProductsByGencodes(crossSells).pipe(
            map((crossProducts) => {
                let accessories: Accessory[] = crossProducts.filter((crossSell) =>
                    ProductFactory.Is(crossSell, Accessory),
                ) as Accessory[];
                accessories = accessories.filter(
                    (accessory) => !accessory.childGencode?.length && accessory.isInStock(),
                );
                this._applyAccessoriesPromotions(cartModel, accessories);
                this.accessories[gencode] = accessories;
                return accessories;
            }),
        );
    }

    private _applyAccessoriesPromotions(cartModel: CheckoutCartModel, accessories: Accessory[]): void {
        accessories.forEach((crossSell: Product) => {
            crossSell.data.enabled = true;
            crossSell.calculatePrices(
                cartModel.getQuote(),
                PromotionsService.GetPromotionsForProduct(crossSell, cartModel.promotions),
            );
        });
    }

    private _sortProductsByList(products: Accessory[], crossSells: LinkedProduct[]): Accessory[] {
        return products.sort((a: Accessory, b: Accessory) =>
            Math.sign(
                this._getPositionByGencode(a.gencode, crossSells) - this._getPositionByGencode(b.gencode, crossSells),
            ),
        );
    }

    private _getPositionByGencode(gencode: string, crossSells: LinkedProduct[]): number {
        const crossSell: LinkedProduct = crossSells.find((up) => up.gencode === gencode);
        return crossSell ? crossSell.position : 0;
    }

    private _canProductHaveCrosssell(product: Product): boolean {
        return ProductFactory.Is(product, Fai) || ProductFactory.Is(product, Phone);
    }
}
